import { ReactNode } from "react";
import { useFacilityContext } from "@/hooks/useFacilityContext";
import { useAuth } from "@/hooks/useAuth";
import { ShiftRequiredModal } from "./ShiftRequiredModal";

interface ShiftRequiredRouteProps {
  children: ReactNode;
  pageName?: string;
  /**
   * Facility to check in to from the gate. Defaults (in ShiftRequiredModal) to
   * the resolved facility context. Pass an explicit id when the protected page
   * knows its target facility (e.g. a resident's facility_id).
   */
  facilityId?: string;
}

/**
 * Route wrapper that requires the user to be on an active shift.
 *
 * Bypass logic:
 *   - No employee record (context.employeeId is null/undefined): the user is a
 *     platform admin or external auditor who literally cannot check in — exempt.
 *   - Has employee record AND isOnShift: user is on duty — allow through.
 *   - Has employee record AND NOT isOnShift: show the check-in gate (all roles).
 *
 * Usage:
 * ```tsx
 * <Route path="/care-notes" element={
 *   <ShiftRequiredRoute pageName="Care Notes">
 *     <CareNotesPage />
 *   </ShiftRequiredRoute>
 * } />
 * ```
 */
export function ShiftRequiredRoute({
  children,
  pageName,
  facilityId,
}: ShiftRequiredRouteProps) {
  const { data: context } = useAuth();
  const { isOnShift } = useFacilityContext();

  // No employee record → cannot check in → exempt from shift requirement.
  if (!context?.employeeId) {
    return <>{children}</>;
  }

  // Has employee record and is on duty → allow through.
  if (isOnShift) {
    return <>{children}</>;
  }

  // Has employee record but not on duty → block with check-in gate.
  return (
    <ShiftRequiredModal
      opened={true}
      pageName={pageName}
      facilityId={facilityId}
    />
  );
}
